import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Badge } from '@/components/common/Badge';
import { authService } from '@/services/auth.service';
import { rolesService, Role } from '@/services/roles.service';
import { orderService } from '@/services/order.service';
import { User } from '@/types/user.types';
import {
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  EyeIcon,
  TrashIcon,
  UserPlusIcon,
  XMarkIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

const ITEMS_PER_PAGE = 10;

interface UserWithStats extends User {
  ordersCount?: number;
  roleName?: string;
}

export const UsersList = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserWithStats[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserWithStats | null>(null);
  const [showActionsMenu, setShowActionsMenu] = useState<number | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState<UserWithStats | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [createFormData, setCreateFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    role_id: null as number | null,
  });
  const [editFormData, setEditFormData] = useState({
    fullName: '',
    email: '',
    role_id: null as number | null,
  });
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger les données
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📝 Chargement des utilisateurs...');
      const [usersData, rolesData, ordersData] = await Promise.all([
        authService.getAllUsers(),
        rolesService.getAll(),
        orderService.getAll(),
      ]);

      console.log('✅ Utilisateurs récupérés:', usersData.length);
      console.log('✅ Rôles récupérés:', rolesData.length);
      console.log('✅ Commandes récupérées:', ordersData.length);

      setRoles(rolesData);

      // Enrichir les utilisateurs avec les statistiques
      const usersWithStats: UserWithStats[] = usersData.map((user) => {
        const role = rolesData.find((r) => r.id === user.role_id);
        const userOrders = ordersData.filter(
          (order) => order.customer_email?.toLowerCase() === user.email.toLowerCase()
        );

        return {
          ...user,
          roleName: role?.name || t('adminUsersList.noRole'),
          ordersCount: userOrders.length,
        };
      });

      setUsers(usersWithStats);
    } catch (error: any) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setError(error.message || t('adminUsersList.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // Charger les données
  useEffect(() => {
    loadData();
  }, []);

  // Fonction pour ouvrir le modal de modification
  const handleEditUser = (user: UserWithStats) => {
    setEditFormData({
      fullName: user.fullName,
      email: user.email,
      role_id: user.role_id || null,
    });
    setEditModalOpen(user);
    setShowActionsMenu(null);
  };

  // Fonction pour mettre à jour un utilisateur
  const handleUpdateUser = async () => {
    if (!editModalOpen) return;

    if (!editFormData.fullName || !editFormData.email) {
      setError(t('adminUsersList.fillRequired'));
      return;
    }

    setUpdating(true);
    setError(null);

    try {
      await authService.updateUser(editModalOpen.id, {
        fullName: editFormData.fullName.trim(),
        email: editFormData.email.trim(),
        role_id: editFormData.role_id || null,
      });

      setEditModalOpen(null);
      await loadData();
      alert(t('adminUsersList.updateSuccess'));
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
      setError(error.message || 'Erreur lors de la mise à jour de l\'utilisateur');
    } finally {
      setUpdating(false);
    }
  };

  // Fonction pour supprimer un utilisateur
  const handleDeleteUser = async (user: UserWithStats) => {
    if (!window.confirm(t('adminUsersList.deleteConfirm', { name: user.fullName, email: user.email }))) {
      return;
    }

    setDeleting(true);
    setShowActionsMenu(null);

    try {
      await authService.deleteUser(user.id);
      await loadData();
      alert(t('adminUsersList.deleteSuccess'));
    } catch (error: any) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      alert(error.message || 'Erreur lors de la suppression de l\'utilisateur');
    } finally {
      setDeleting(false);
    }
  };

  // Fonction pour créer un utilisateur
  const handleCreateUser = async () => {
    if (!createFormData.fullName || !createFormData.email || !createFormData.password) {
      setError(t('adminUsersList.fillRequired'));
      return;
    }

    if (createFormData.password.length < 6) {
      setError(t('adminUsersList.passwordMinError'));
      return;
    }

    setCreating(true);
    setError(null);

    try {
      // Utiliser createUser qui permet de spécifier le rôle (pour les admins)
      await authService.createUser({
        fullName: createFormData.fullName.trim(),
        email: createFormData.email.trim(),
        password: createFormData.password,
        role_id: createFormData.role_id || null, // null = rôle 'user' par défaut
      });

      // Réinitialiser le formulaire
      setCreateFormData({
        fullName: '',
        email: '',
        password: '',
        role_id: null,
      });
      setCreateModalOpen(false);

      // Recharger les données
      await loadData();

      alert(t('adminUsersList.createSuccess'));
    } catch (error: any) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      setError(error.message || 'Erreur lors de la création de l\'utilisateur');
    } finally {
      setCreating(false);
    }
  };

  // Filtrer les utilisateurs
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query) ||
        (user.roleName && user.roleName.toLowerCase().includes(query))
    );
  }, [users, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fonction pour obtenir les initiales
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fonction pour obtenir la couleur du badge selon le rôle
  const getRoleBadgeVariant = (roleName: string): 'success' | 'error' | 'warning' | 'info' | 'neutral' => {
    const roleLower = roleName.toLowerCase();
    if (roleLower.includes('admin')) return 'error';
    if (roleLower.includes('manager') || roleLower.includes('editor')) return 'warning';
    if (roleLower.includes('user') || roleLower.includes('client')) return 'success';
    return 'neutral';
  };

  // Fonction pour formater la date
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Colonnes du tableau
  const columns: ResponsiveTableColumn<UserWithStats>[] = [
    {
      key: 'user',
      label: t('adminUsersList.name'),
      render: (user) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#8C1843',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: '600',
              fontSize: '14px',
              flexShrink: 0,
            }}
          >
            {getInitials(user.fullName)}
          </div>
          <div>
            <div className="table-cell-name" style={{ fontWeight: '600' }}>
              {user.fullName}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {user.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: t('adminUsersList.role'),
      render: (user) => (
        <Badge variant={getRoleBadgeVariant(user.roleName || '')}>
          {user.roleName || t('adminUsersList.noRole')}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: t('adminUsersList.registerDate'),
      render: (user) => <div className="table-cell-text">{formatDate(user.createdAt)}</div>,
      mobileHidden: true,
    },
    {
      key: 'ordersCount',
      label: t('adminUsersList.orders'),
      render: (user) => (
        <div className="table-cell-number" style={{ fontWeight: '600' }}>
          {user.ordersCount || 0}
        </div>
      ),
    },
    {
      key: 'actions',
      label: t('adminUsersList.actions'),
      render: (user) => (
        <div style={{ position: 'relative' }}>
          <button
            className="action-btn"
            onClick={(e) => {
              e.stopPropagation();
              setShowActionsMenu(showActionsMenu === user.id ? null : user.id || 0);
            }}
            style={{
              padding: '8px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              borderRadius: '4px',
            }}
          >
            <EllipsisVerticalIcon className="w-5 h-5" style={{ color: '#666' }} />
          </button>
          {showActionsMenu === user.id && (
            <>
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 999,
                }}
                onClick={() => setShowActionsMenu(null)}
              />
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '160px',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => {
                    setSelectedUser(user);
                    setShowActionsMenu(null);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#333',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <EyeIcon className="w-4 h-4" />
                  {t('adminUsersList.viewProfile')}
                </button>
                <button
                  onClick={() => handleEditUser(user)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#333',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <PencilIcon className="w-4 h-4" />
                  {t('adminUsersList.edit')}
                </button>
                <button
                  onClick={() => handleDeleteUser(user)}
                  disabled={deleting}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: deleting ? '#999' : '#dc2626',
                    opacity: deleting ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#fee')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <TrashIcon className="w-4 h-4" />
                  {deleting ? t('adminUsersList.deleting') : t('adminUsersList.delete')}
                </button>
              </div>
            </>
          )}
        </div>
      ),
      mobileHidden: true,
    },
  ];

  return (
    <>

    {/* En-tête */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">
                  {t('adminUsersList.title', { count: filteredUsers.length })}
                </h3>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setCreateModalOpen(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <UserPlusIcon className="w-4 h-4" />
                  {t('adminUsersList.addUser')}
                </button>
              </div>

              {/* Messages d'erreur */}
              {error && !loading && (
                <div
                  style={{
                    padding: '12px',
                    margin: '16px',
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    color: '#c33',
                  }}
                >
                  {error}
                </div>
              )}

              {/* Barre de recherche */}
              <div className="filters-bar">
                <div className="form-group-flex" style={{ flex: 1, maxWidth: '400px' }}>
                  <label className="form-label">{t('adminUsersList.search')}</label>
                  <div style={{ position: 'relative' }}>
                    <MagnifyingGlassIcon
                      className="w-5 h-5"
                      style={{
                        position: 'absolute',
                        left: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#999',
                        pointerEvents: 'none',
                      }}
                    />
                    <input
                      type="text"
                      placeholder={t('adminUsersList.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="form-input"
                      style={{ paddingLeft: '40px' }}
                    />
                  </div>
                </div>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="btn btn-secondary btn-sm"
                  >
                    {t('dashboardPage.reset')}
                  </button>
                )}
              </div>

              {/* Tableau */}
              <ResponsiveTable
                columns={columns}
                data={paginatedUsers}
                loading={loading}
                loadingMessage={t('adminUsersList.loading')}
                emptyMessage={
                  searchQuery
                    ? t('adminUsersList.noSearch')
                    : t('adminUsersList.empty')
                }
                mobileCardTitle={(user) => (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#8C1843',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: '600',
                          fontSize: '14px',
                        }}
                      >
                        {getInitials(user.fullName)}
                      </div>
                      <div>
                        <div className="table-cell-name">{user.fullName}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                      </div>
                    </div>
                  </div>
                )}
                mobileCardActions={(user) => (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setSelectedUser(user)}
                  >
                    {t('adminUsersList.viewProfile')}
                  </button>
                )}
                tableClassName="products-table"
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  className="pagination-container"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '24px',
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminUsersList.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredUsers.length), total: filteredUsers.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminUsersList.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
    <>

                            <span key={page} style={{ padding: '0 4px' }}>
                              ...
                            </span>
                          
    </>
  );
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminUsersList.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Modal de détails utilisateur */}
            {selectedUser && (
              <div
                className="modal-overlay"
                onClick={() => setSelectedUser(null)}
                role="dialog"
                aria-modal="true"
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: '600px' }}
                >
                  <div className="modal-header">
                    <h3 className="modal-title">{t('adminUsersList.profileTitle', { name: selectedUser.fullName })}</h3>
                    <button className="modal-close" onClick={() => setSelectedUser(null)}>
                      ×
                    </button>
                  </div>
                  <div className="modal-body">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div
                          style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#8C1843',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '600',
                            fontSize: '32px',
                          }}
                        >
                          {getInitials(selectedUser.fullName)}
                        </div>
                        <div>
                          <h4 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '4px' }}>
                            {selectedUser.fullName}
                          </h4>
                          <p style={{ fontSize: '14px', color: '#666' }}>{selectedUser.email}</p>
                        </div>
                      </div>

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '16px',
                          paddingTop: '20px',
                          borderTop: '1px solid #e0e0e0',
                        }}
                      >
                        <div>
                          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                            {t('adminUsersList.role')}
                          </label>
                          <div>
                            <Badge variant={getRoleBadgeVariant(selectedUser.roleName || '')}>
                              {selectedUser.roleName || t('adminUsersList.noRole')}
                            </Badge>
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                            {t('adminUsersList.registerDate')}
                          </label>
                          <div style={{ fontSize: '14px', color: '#333' }}>
                            {formatDate(selectedUser.createdAt)}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                            {t('adminUsersList.userId')}
                          </label>
                          <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                            #{selectedUser.id}
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '4px' }}>
                            {t('adminUsersList.ordersCountLabel')}
                          </label>
                          <div style={{ fontSize: '14px', color: '#333', fontWeight: '600' }}>
                            {selectedUser.ordersCount || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setSelectedUser(null)}>
                      {t('adminUsersList.close')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de création d'utilisateur */}
            {createModalOpen && (
              <div
                className="modal-overlay"
                onClick={() => !creating && setCreateModalOpen(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-user-modal-title"
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: '500px' }}
                >
                  <div className="modal-header">
                    <h3 id="create-user-modal-title" className="modal-title">
                      {t('adminUsersList.modalCreate')}
                    </h3>
                    <button
                      className="modal-close"
                      onClick={() => !creating && setCreateModalOpen(false)}
                      aria-label={t('adminUsersList.close')}
                      disabled={creating}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {error && (
                      <div
                        style={{
                          padding: '12px',
                          marginBottom: '16px',
                          backgroundColor: '#fee',
                          border: '1px solid #fcc',
                          borderRadius: '4px',
                          color: '#c33',
                        }}
                      >
                        {error}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="form-group">
                        <label className="form-label">
                          Nom complet <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          value={createFormData.fullName}
                          onChange={(e) =>
                            setCreateFormData({ ...createFormData, fullName: e.target.value })
                          }
                          disabled={creating}
                          placeholder={t('adminUsersList.namePlaceholder')}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Email <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="email"
                          className="form-input"
                          value={createFormData.email}
                          onChange={(e) =>
                            setCreateFormData({ ...createFormData, email: e.target.value })
                          }
                          disabled={creating}
                          placeholder={t('adminUsersList.emailPlaceholder')}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Mot de passe <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="password"
                          className="form-input"
                          value={createFormData.password}
                          onChange={(e) =>
                            setCreateFormData({ ...createFormData, password: e.target.value })
                          }
                          disabled={creating}
                          placeholder={t('adminUsersList.passwordPlaceholder')}
                          required
                        />
                        <small style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
                          {t('adminUsersList.passwordHint')}
                        </small>
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {t('adminUsersList.role')}
                        </label>
                        <select
                          className="form-input"
                          value={createFormData.role_id || ''}
                          onChange={(e) =>
                            setCreateFormData({ 
                              ...createFormData, 
                              role_id: e.target.value ? parseInt(e.target.value) : null 
                            })
                          }
                          disabled={creating}
                        >
                          <option value="">{t('adminUsersList.selectRoleDefault')}</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <small style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
                          {t('adminUsersList.defaultRoleHint')}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div
                    className="modal-footer"
                    style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px' }}
                  >
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCreateModalOpen(false)}
                      disabled={creating}
                    >
                      {t('adminUsersList.cancel')}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleCreateUser}
                      disabled={
                        creating ||
                        !createFormData.fullName ||
                        !createFormData.email ||
                        !createFormData.password
                      }
                    >
                      {creating ? t('adminUsersList.creating') : t('adminUsersList.create')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de modification d'utilisateur */}
            {editModalOpen && (
              <div
                className="modal-overlay"
                onClick={() => !updating && setEditModalOpen(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="edit-user-modal-title"
              >
                <div
                  className="modal-content"
                  onClick={(e) => e.stopPropagation()}
                  style={{ maxWidth: '500px' }}
                >
                  <div className="modal-header">
                    <h3 id="edit-user-modal-title" className="modal-title">
                      {t('adminUsersList.modalEdit')}
                    </h3>
                    <button
                      className="modal-close"
                      onClick={() => !updating && setEditModalOpen(null)}
                      aria-label={t('adminUsersList.close')}
                      disabled={updating}
                    >
                      <XMarkIcon className="btn-icon" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {error && (
                      <div
                        style={{
                          padding: '12px',
                          marginBottom: '16px',
                          backgroundColor: '#fee',
                          border: '1px solid #fcc',
                          borderRadius: '4px',
                          color: '#c33',
                        }}
                      >
                        {error}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div className="form-group">
                        <label className="form-label">
                          Nom complet <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="text"
                          className="form-input"
                          value={editFormData.fullName}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, fullName: e.target.value })
                          }
                          disabled={updating}
                          placeholder={t('adminUsersList.namePlaceholder')}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Email <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <input
                          type="email"
                          className="form-input"
                          value={editFormData.email}
                          onChange={(e) =>
                            setEditFormData({ ...editFormData, email: e.target.value })
                          }
                          disabled={updating}
                          placeholder={t('adminUsersList.emailPlaceholder')}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          {t('adminUsersList.role')}
                        </label>
                        <select
                          className="form-input"
                          value={editFormData.role_id || ''}
                          onChange={(e) =>
                            setEditFormData({ 
                              ...editFormData, 
                              role_id: e.target.value ? parseInt(e.target.value) : null 
                            })
                          }
                          disabled={updating}
                        >
                          <option value="">{t('adminUsersList.noRole')}</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                        <small style={{ fontSize: '12px', color: '#666', marginTop: '4px', display: 'block' }}>
                          Sélectionnez un rôle pour cet utilisateur. Si aucun rôle n'est sélectionné, l'utilisateur n'aura pas de rôle.
                        </small>
                      </div>
                    </div>
                  </div>
                  <div
                    className="modal-footer"
                    style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', padding: '16px' }}
                  >
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditModalOpen(null)}
                      disabled={updating}
                    >
                      {t('adminUsersList.cancel')}
                    </button>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleUpdateUser}
                      disabled={
                        updating ||
                        !editFormData.fullName ||
                        !editFormData.email
                      }
                    >
                      {updating ? 'Mise à jour...' : 'Enregistrer'}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};

